/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useRef } from "react";
import { BEM, useToggle } from "@adapptio/lib-components";
import { FC_ANCHOR, FC_DIRECTION } from "../../core";
import {
	FIELD_FLOATING_PANEL_SCHEMA,
	FIELD_FLOATING_PANEL_STYLE,
	FieldFloatingPanel
} from "../FieldFloatingPanel";
import { FieldWrapper } from "../FieldWrapper";
import { IFieldValidationError } from "../FieldTypes";
import { ResourceInput } from "../../core/ResourceInput";

export interface IFieldRouteSelectorProps {
	value: string;
	placeholder?: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	grow?: boolean;
	listAnchor?: FC_ANCHOR;
	listDirection?: FC_DIRECTION;
	listOffset?: number;
	onChange?: (value: string) => void;
}

export const FieldRouteSelector: React.FC<IFieldRouteSelectorProps> = ({
	value,
	placeholder,
	readOnly,
	validationErrors,
	className,
	grow,
	listAnchor,
	listDirection,
	listOffset,
	onChange
}) => {
	const baseClass = BEM("edt-field-route-selector");

	const [ listOpened, toggleList, openList, closeList ] = useToggle(false);
	const elRef = useRef<HTMLDivElement>();

	if (className) {
		baseClass.add(className);
	}

	return (
		<FieldWrapper type="route-selector" className={className}>
			<ResourceInput onClick={toggleList} disabled={readOnly} ref={elRef}>
				{value ?? placeholder}
			</ResourceInput>

			<FieldFloatingPanel
				for={elRef}
				isOpened={listOpened}
				anchor={listAnchor || FC_ANCHOR.RIGHT_BOTTOM}
				direction={listDirection || FC_DIRECTION.HR}
				style={FIELD_FLOATING_PANEL_STYLE.STICKY}
				schema={FIELD_FLOATING_PANEL_SCHEMA.PRIMARY}
				offset={listOffset || 0}
				className="edt-field--type-icon-selector__panel"
				onClose={closeList}
			>
				<div className={baseClass.element("icon-list").toClassName()}></div>
			</FieldFloatingPanel>
		</FieldWrapper>
	);
};
